import React, { useCallback, useEffect, useState } from "react";
import { CloseIconButton, PreLoading } from "@tripian/react";

interface IToristyTourInfoEmbed {
  tourId: string;
  onClose: () => void;
  apiKey: string;
  currency?: string;
  lang?: string;
  mode?: "buttononly" | "toolwithreviews";
}

const ToristyTourInfoEmbed: React.FC<IToristyTourInfoEmbed> = ({
  tourId,
  onClose,
  apiKey,
  currency = "usd",
  lang = "en",
  mode = "buttononly",
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const containerId = `toristyiframe-responsive-${tourId}`;
  const scriptId = `toristy-script-${tourId}`;

  const handleClose = useCallback(() => {
    document.body.style.overflow = "auto";
    setIsLoading(true);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://embed.toristy.com") return;
      if (
        typeof event.data === "string" &&
        event.data.startsWith("ToristyAction|launchEmbedCheckout|")
      ) {
        handleClose();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleClose, onClose]);

  useEffect(() => {
    const oldScript = document.getElementById(scriptId);
    try {
      if (oldScript) oldScript.remove();
    } catch (error)
    {
      console.info("ToristyEmbed");
    }

    // embed.js redefines toristy.toristyAction and adds it as a new message listener on every load.
    // Drop the previous one first, otherwise each reopen stacks a listener and checkout launches multiple times.
    const toristyGlobal = (window as unknown as { toristy?: { toristyAction?: EventListener } }).toristy;
    if (toristyGlobal?.toristyAction) {
      window.removeEventListener("message", toristyGlobal.toristyAction, false);
    }

    const script = document.createElement("script");
    const params = new URLSearchParams({
      es: `#${containerId}`,
      serviceid: tourId,
      apikey: apiKey,
      ...(currency ? { currency } : {}),
      ...(lang ? { lang } : {}),
      ...(mode ? { ct: mode } : {}),
    });

    script.src = `https://embed.toristy.com/embed.js?${params.toString()}`;
    script.async = true;
    script.charset = "utf-8";
    script.id = scriptId;
    document.body.appendChild(script);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeName === "IFRAME") {
            (node as HTMLIFrameElement).onload = () => {
              setIsLoading(false);
              observer.disconnect();
            };
          }
        });
      }
    });

    const containerEl = document.getElementById(containerId);
    if (containerEl) {
      observer.observe(containerEl, { childList: true });
    }

    return () => {
      setIsLoading(true);
      observer.disconnect();
    };
  }, [tourId, apiKey, currency, lang, mode, scriptId, containerId]);

  useEffect(() => {
    return () => {
      const oldScript = document.getElementById(scriptId);
      try {
        if (oldScript) oldScript.remove();
      } catch (error)
      {
        console.info("ToristyEmbed");
      }

      const containerEl = document.getElementById(containerId);
      if (containerEl) containerEl.innerHTML = "";
    };
  }, [containerId, scriptId]);

  return (
    <div className="fixed left-0 right-0 bottom-0 top-[60px] md:top-[90px] z-[9999] bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-4xl bg-white rounded-[2.5rem] shadow-lg p-6 relative my-4 mx-2 md:mx-0 max-h-[calc(100vh-60px-2rem)] md:max-h-[calc(100vh-90px-2rem)] flex flex-col">
        <div className="flex items-center justify-end w-full md:mb-6 mb-4 px-2">
          <CloseIconButton
            className="w-6 h-6"
            fill="#2B2B33"
            clicked={handleClose}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-[600px]">
              <PreLoading />
            </div>
          )}
          <div
            id={containerId}
            className="min-h-[600px]"
            style={{ display: isLoading ? "none" : "block" }}
          />
        </div>
      </div>
    </div>
  );
};

export default ToristyTourInfoEmbed;
